import {
  Avatar,
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import type { IFeatures } from "@/utils/interfaces";

import { UserMenuPanelContent } from "./UserMenuPanelContent";

interface UserMenuDesktopPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  userName?: string | null;
  userUsername?: string | null;
  accessLabel: string;
  initials: string;
  menuItems: IFeatures[];
  onNavigate: (path?: string) => void;
  onOpenSupport: () => void;
  onOpenTerms: () => void;
  onLogout: () => void;
}

export function UserMenuDesktopPanel({
  open,
  anchorEl,
  onClose,
  userName,
  userUsername,
  accessLabel,
  initials,
  menuItems,
  onNavigate,
  onOpenSupport,
  onOpenTerms,
  onLogout,
}: UserMenuDesktopPanelProps) {
  const theme = useTheme();

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      transition
      style={{ zIndex: theme.zIndex.modal + 1 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={180}>
          <Box>
            <ClickAwayListener
              onClickAway={(event) => {
                const target = event.target as Node;
                if (anchorEl?.contains(target)) return;
                onClose();
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  mt: 1,
                  width: 320,
                  maxWidth: "calc(100vw - 24px)",
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 24,
                  borderBottomRightRadius: 24,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 16px 40px rgba(0, 0, 0, 0.12)",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.25}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "primary800.main",
                    color: "neutral0.main",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "neutral0.main",
                      color: "primary800.main",
                      fontWeight: 700,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      noWrap
                      sx={{ color: "inherit" }}
                    >
                      {userName || "Usuário"}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: "rgba(255,255,255,0.78)" }}
                    >
                      {accessLabel}
                      {userUsername ? ` · ${userUsername}` : ""}
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ px: 1.25, py: 1.25, pb: 1.5 }}>
                  <UserMenuPanelContent
                    userName={userName}
                    userUsername={userUsername}
                    accessLabel={accessLabel}
                    initials={initials}
                    menuItems={menuItems}
                    showProfileHero={false}
                    onNavigate={onNavigate}
                    onOpenSupport={onOpenSupport}
                    onOpenTerms={onOpenTerms}
                    onLogout={onLogout}
                  />
                </Box>
              </Paper>
            </ClickAwayListener>
          </Box>
        </Fade>
      )}
    </Popper>
  );
}
